import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCapabilities } from '../../hooks/useCapabilities';
import { formatMoney } from '../../utils/formatMoney';
import { useDashboardOverview } from './hooks/useDashboardOverview';
import type { DashboardPeriod } from './types';
import { DashboardAngeboteChart } from './components/DashboardAngeboteChart';
import { DashboardEmptyState } from './components/DashboardEmptyState';
import { DashboardHeader } from './components/DashboardHeader';
import { DashboardKpiCard } from './components/DashboardKpiCard';
import { DashboardLoadingSkeleton } from './components/DashboardLoadingSkeleton';
import { DashboardPipelinePanel } from './components/DashboardPipelinePanel';
import { DashboardStatCard } from './components/DashboardStatCard';
import { kpiComparisonCaption } from './utils/kpiComparisonLabel';
import { offerAcceptance, openOfferSummary } from './utils/offerSummary';

const VERSENDET_LABEL: Record<DashboardPeriod, string> = {
  today: 'Heute versendet',
  week: 'Versendet',
  month: 'Versendet',
};

/** Angebote zuerst; Anfragen als zweite Ansicht, Umsatz gibt es ohne Rechnungen nicht. */
const CHART_MODES = { umsatz: false, angebote: true, anfragen: true };

function formatRate(rate: number | null): string {
  if (rate == null) return '–';
  return `${(rate * 100).toLocaleString('de-DE', { maximumFractionDigits: 0 })} %`;
}

/**
 * Übersicht für das Profil 'angebote' (Angebots-Workflow ohne Rechnungen und Auftragsabwicklung):
 * offene Angebote, Annahmequote, versendete Angebote, Angebots-Diagramm und Pipeline.
 */
export function OfferDashboardContent() {
  const [period, setPeriod] = useState<DashboardPeriod>('month');
  const { data, loading, refreshing, error, lastUpdatedAt, refetch } = useDashboardOverview(period);
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();
  const isPeriodSwitching = refreshing && data?.period !== period;

  const canViewProjects = can('web.projekte.view');
  const canCreateOffer = can('web.projekte.edit');
  const showToday = can('web.plantafel.view');

  const openOffers = useMemo(() => (data ? openOfferSummary(data.pipeline) : null), [data]);
  const acceptance = useMemo(() => (data ? offerAcceptance(data.pipeline) : null), [data]);

  if (loading || !capabilitiesLoaded) {
    return <DashboardLoadingSkeleton />;
  }

  if (!data || !openOffers || !acceptance) {
    return (
      <DashboardEmptyState
        message={error ?? 'Dashboard konnte nicht geladen werden.'}
        onRetry={refetch}
      />
    );
  }

  const handleCreateOffer = () => {
    navigate(`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/list?neuesAngebot=1`);
  };

  const sent = data.kpis.angeboteVersendet;

  return (
    <div className={`space-y-5 transition-opacity ${refreshing ? 'opacity-60' : 'opacity-100'}`}>
      <DashboardHeader
        greeting={data.greeting}
        period={period}
        onPeriodChange={setPeriod}
        onCreateProject={canCreateOffer ? handleCreateOffer : undefined}
        createLabel="Neues Angebot"
        showAppointmentsToday={showToday}
        refreshing={refreshing}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
      />
      {isPeriodSwitching ? (
        <DashboardLoadingSkeleton />
      ) : !canViewProjects ? (
        <div className="rounded-xl border border-app-border bg-app-surface p-8 text-center text-sm text-app-text-muted shadow-sm">
          Keine Übersicht verfügbar für deine Rechte.
        </div>
      ) : (
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <DashboardStatCard
              label="Offene Angebote"
              value={openOffers.count.toLocaleString('de-DE')}
              detail={`${formatMoney(openOffers.volume)} Volumen`}
            />
            <DashboardStatCard
              label="Annahmequote"
              value={formatRate(acceptance.rate)}
              detail={`${acceptance.won.toLocaleString('de-DE')} gewonnen · ${acceptance.lost.toLocaleString('de-DE')} verloren`}
            />
            {sent ? (
              <DashboardKpiCard
                label={VERSENDET_LABEL[data.period]}
                kpi={sent}
                comparisonLabel={kpiComparisonCaption(data.kpiComparison)}
                detail={`${formatMoney(sent.grossSum)} brutto`}
              />
            ) : null}
          </div>
          <DashboardAngeboteChart
            points={data.chart.points}
            subtitle={data.chart.subtitle}
            availableModes={CHART_MODES}
          />
          <DashboardPipelinePanel pipeline={data.pipeline} categories={data.categories} />
        </div>
      )}
    </div>
  );
}
